import React, { useEffect, useState } from 'react';

import { CircularProgressbarWithChildren, buildStyles } from 'react-circular-progressbar';
import { useMediaQuery } from 'react-responsive';
import { useNavigate, useLocation } from 'react-router-dom';
import Text from 'common/components/Text/Text';
import { countMatchedElements, navigateTop, userInitials } from 'common/utils';
import { SessionState } from 'features/session/sessionSlice';
import { setLastStepDone, setProfileProgress, UserState } from 'features/user/userSlice';
import { useAppDispatch, useAppSelector } from 'app/hooks';
import { setProfileCompleted } from 'features/user/userSlice';
import { useUpdateUserMutation, useUpdateBackofficeUserMutation } from 'services/exploreApi';
import styles from './Dashboard.module.scss';
import { Profile1, Profile2, Profile3, Profile4 } from 'assets/session/dashboard/index';
import {
  ACADEMIC_FORM_ANSWERS,
  PERSONAL_FORM_ANSWERS,
  TESTS_FORM_ANSWERS,
  WORK_FORM_ANSWERS,
} from 'common/constants';

const ProfileCard = (): React.ReactElement => {
  const location = useLocation();
  const { user } = useAppSelector(({ session }: { session: SessionState }) => session);
  const { profileProgress, profileCompleted } = useAppSelector(
    ({ user }: { user: UserState }) => user
  );
  let updateUser: any;
  if (location.pathname.includes('backoffice')) {
    [updateUser] = useUpdateBackofficeUserMutation();
  } else {
    [updateUser] = useUpdateUserMutation();
  }
  const isMobile = useMediaQuery({ maxWidth: 768 });
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [progress, setProgress] = useState<boolean>(profileCompleted || false);

  const profileTotalProgress = () => {
    let total = 0;
    Object.keys(profileProgress).forEach((key) => {
      total += profileProgress[key];
    });
    const result = Math.round((total / 4) * 100);
    setTimeout(() => {
      if (result === 100) {
        !profileCompleted ? dispatch(setLastStepDone(1)) : '';
        dispatch(setProfileCompleted(true));
        setProgress(true);
      } else {
        dispatch(setProfileCompleted(false));
      }
    }, 100);
    return result;
  };
  const updateProfileData = () => {
    if (user) {
      const profileProgress = {
        personal: countMatchedElements(PERSONAL_FORM_ANSWERS, user) / PERSONAL_FORM_ANSWERS.length,
        academic: user.academic_history?.[0]
          ? countMatchedElements(ACADEMIC_FORM_ANSWERS, user, 'academic_history') /
            ACADEMIC_FORM_ANSWERS.length
          : 0,
        work: user.work_history?.[0]
          ? user.have_working_experience
            ? countMatchedElements(WORK_FORM_ANSWERS, user, 'work_history') /
              WORK_FORM_ANSWERS.length
            : 1
          : 0,
        tests:
          countMatchedElements(TESTS_FORM_ANSWERS, user, 'english_exams') /
          TESTS_FORM_ANSWERS.length,
      };
      dispatch(setProfileProgress(profileProgress));
    }
  };

  useEffect(() => {
    updateProfileData();
  }, [user]);

  useEffect(() => {
    if (progress === true && user && !user.profile_completed) {
      const formData = new FormData();
      formData.append('user[profile_completed]', String(true));
      updateUser({ data: formData, id: user.id });
    }
  }, [progress]);

  return (
    <>
      {isMobile ? (
        <div className={styles.myProfileBox}>
          <div className={styles.profileContainer}>
            <Text size={16} weight={500}>
              My profile
            </Text>
            <div className={styles.profileGraphic}>
              <CircularProgressbarWithChildren
                value={profileTotalProgress()}
                strokeWidth={7}
                styles={buildStyles({
                  pathColor: '#4D8DF4',
                  trailColor: '#EFF3FD',
                  backgroundColor: 'inherit',
                })}
              >
                <div className={styles.userPhoto}>
                  {user?.photo ? (
                    <img src={user.photo} className={styles.userAvatarImg}></img>
                  ) : (
                    userInitials(user)
                  )}
                </div>
              </CircularProgressbarWithChildren>
            </div>
          </div>

          <div className={styles.infoContainer}>
            {profileCompleted ? (
              <div className={styles.profileFinishedRow}>
                <Text size={16} weight={500} color="primary">
                  Profile finished
                </Text>
                <i
                  className="bi bi-pencil-square"
                  onClick={() => {
                    navigate('/home/personal_information');
                    navigateTop();
                  }}
                ></i>
              </div>
            ) : (
              <div>
                <div className={styles.graphicsContainer}>
                  <div className={styles.graphic}>
                    <CircularProgressbarWithChildren
                      value={profileProgress.personal ? profileProgress.personal * 100 : 0}
                      strokeWidth={8}
                      styles={buildStyles({
                        pathColor: '#4D8DF4',
                        trailColor: '#E3EAFB',
                        backgroundColor: 'inherit',
                      })}
                    >
                      <img src={Profile1} className={styles.graphicIcon}></img>
                    </CircularProgressbarWithChildren>
                  </div>
                  <div className={styles.graphic}>
                    <CircularProgressbarWithChildren
                      value={profileProgress.academic ? profileProgress.academic * 100 : 0}
                      strokeWidth={8}
                      styles={buildStyles({
                        pathColor: '#4D8DF4',
                        trailColor: '#E3EAFB',
                        backgroundColor: 'inherit',
                      })}
                    >
                      <img src={Profile2} className={styles.graphicIcon}></img>
                    </CircularProgressbarWithChildren>
                  </div>
                  <div className={styles.graphic}>
                    <CircularProgressbarWithChildren
                      value={profileProgress.work ? profileProgress.work * 100 : 0}
                      strokeWidth={8}
                      styles={buildStyles({
                        pathColor: '#4D8DF4',
                        trailColor: '#E3EAFB',
                        backgroundColor: 'inherit',
                      })}
                    >
                      <img src={Profile3} className={styles.graphicIcon}></img>
                    </CircularProgressbarWithChildren>
                  </div>
                  <div className={styles.graphic}>
                    <CircularProgressbarWithChildren
                      value={profileProgress.tests ? profileProgress.tests * 100 : 0}
                      strokeWidth={8}
                      styles={buildStyles({
                        pathColor: '#4D8DF4',
                        trailColor: '#E3EAFB',
                        backgroundColor: 'inherit',
                      })}
                    >
                      <img src={Profile4} className={styles.graphicIcon}></img>
                    </CircularProgressbarWithChildren>
                  </div>
                </div>
                <div
                  className={styles.textContainer}
                  onClick={() => {
                    navigate('personal_information');
                    navigateTop();
                  }}
                >
                  <Text size={10} weight={600} color="secondary">
                    🚀 Finish Profile To Get the Full
                  </Text>
                  <Text size={10} weight={600} color="secondary">
                    Services of Unify! <i className="bi bi-arrow-right"></i>
                  </Text>
                </div>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className={styles.myProfileBox}>
          <div className={styles.profileGraphic}>
            <CircularProgressbarWithChildren
              value={profileTotalProgress()}
              strokeWidth={7}
              styles={buildStyles({
                pathColor: '#4D8DF4',
                trailColor: '#EFF3FD',
                backgroundColor: 'inherit',
              })}
            >
              <div className={styles.userPhoto}>
                {user?.photo ? (
                  <img src={user.photo} className={styles.userAvatarImg}></img>
                ) : (
                  userInitials(user)
                )}
              </div>
            </CircularProgressbarWithChildren>
          </div>
          <div className={styles.name}>{user?.name}</div>
          <div className={styles.infoContainer}>
            {profileCompleted ? (
              <div className={styles.profileFinishedRow}>
                <Text size={16} weight={500} color="primary">
                  Profile finished
                </Text>
                <i
                  className="bi bi-pencil-square"
                  onClick={() => {
                    navigate('personal_information');
                    navigateTop();
                  }}
                ></i>
              </div>
            ) : (
              <div>
                <div className={styles.graphicsContainer}>
                  <div className={styles.graphic}>
                    <CircularProgressbarWithChildren
                      value={profileProgress.personal ? profileProgress.personal * 100 : 0}
                      strokeWidth={8}
                      styles={buildStyles({
                        pathColor: '#4D8DF4',
                        trailColor: '#E3EAFB',
                        backgroundColor: 'inherit',
                      })}
                    >
                      <img src={Profile1} className={styles.graphicIcon}></img>
                    </CircularProgressbarWithChildren>
                  </div>
                  <div className={styles.graphic}>
                    <CircularProgressbarWithChildren
                      value={profileProgress.academic ? profileProgress.academic * 100 : 0}
                      strokeWidth={8}
                      styles={buildStyles({
                        pathColor: '#4D8DF4',
                        trailColor: '#E3EAFB',
                        backgroundColor: 'inherit',
                      })}
                    >
                      <img src={Profile2} className={styles.graphicIcon}></img>
                    </CircularProgressbarWithChildren>
                  </div>
                  <div className={styles.graphic}>
                    <CircularProgressbarWithChildren
                      value={profileProgress.work ? profileProgress.work * 100 : 0}
                      strokeWidth={8}
                      styles={buildStyles({
                        pathColor: '#4D8DF4',
                        trailColor: '#E3EAFB',
                        backgroundColor: 'inherit',
                      })}
                    >
                      <img src={Profile3} className={styles.graphicIcon}></img>
                    </CircularProgressbarWithChildren>
                  </div>
                  <div className={styles.graphic}>
                    <CircularProgressbarWithChildren
                      value={profileProgress.tests ? profileProgress.tests * 100 : 0}
                      strokeWidth={8}
                      styles={buildStyles({
                        pathColor: '#4D8DF4',
                        trailColor: '#E3EAFB',
                        backgroundColor: 'inherit',
                      })}
                    >
                      <img src={Profile4} className={styles.graphicIcon}></img>
                    </CircularProgressbarWithChildren>
                  </div>
                </div>
                <div
                  className={styles.finishButton}
                  onClick={() => {
                    navigate('personal_information');
                    navigateTop();
                  }}
                >
                  Finish Profile
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default ProfileCard;
